import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createSchemaScope, isRichTextDoc, richTextFromTemplate, richTextToPlain, parseExpression, labelForPath, type RichTextDoc } from "@broadpaper/core";
import type { EditorView } from "prosemirror-view";
import { useSession, useNode, useLayoutVersion } from "../state/context.js";
import { createRichTextView, activeFormats, rteCommands, type ActiveFormats } from "../richtext/editor.js";
import { TokenSuggestions } from "../richtext/TokenSuggestions.js";
import type { AutocompleteHandlers, TokenTrigger } from "../richtext/autocomplete.js";
import { firstVisibleElement } from "./geometry.js";
import { Button } from "../ui/primitives.js";
import { Popover } from "../ui/floating.js";
import { DataPicker } from "../inspector/DataPicker.js";
import { ColorPicker } from "../inspector/fields/ColorField.js";
import { TextInput } from "../ui/primitives.js";

interface Props {
  nodeId: string;
  container: HTMLElement;
  origin: HTMLElement;
  zoom: number;
  onClose: () => void;
}

/**
 * ProseMirror overlay placed exactly over a text or heading block. Edits are
 * written to the store as one merged undo step per typing burst.
 */
export function InlineEditor({ nodeId, container, origin, zoom, onClose }: Props) {
  const session = useSession();
  const entry = useNode(nodeId);
  const version = useLayoutVersion();
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<ReturnType<typeof createRichTextView> | null>(null);
  const [formats, setFormats] = useState<ActiveFormats | null>(null);
  const [box, setBox] = useState<{ left: number; top: number; width: number; minHeight: number; style: string } | null>(null);
  // A "{{" the user is part way through typing, and where to anchor its list.
  const [suggest, setSuggest] = useState<{ trigger: TokenTrigger; view: EditorView; at: { left: number; top: number } } | null>(null);
  const handlersRef = useRef<AutocompleteHandlers | null>(null);
  const def = entry ? session.registry.get(entry.node.type) : undefined;
  const inline = def?.canvas?.inlineEdit;
  const prop = inline?.prop ?? "content";
  const singleLine = inline?.kind === "text";

  const labelFor = (expr: string) => {
    try {
      const ast = parseExpression(expr);
      const scope = createSchemaScope(session.dataSources);
      if (ast.kind === "path") return labelForPath(ast.segments, scope);
      if (ast.kind === "pipe" && ast.input.kind === "path") return `${labelForPath(ast.input.segments, scope)} · ${ast.name}`;
    } catch {
      /* fall through */
    }
    return expr;
  };

  // Position over the first visible copy of the node; copy its computed text styles.
  useLayoutEffect(() => {
    const el = firstVisibleElement(container, nodeId);
    if (!el) return;
    const textEl = (el.querySelector<HTMLElement>("[data-part='text']") ?? el.firstElementChild ?? el) as HTMLElement;
    const r = textEl.getBoundingClientRect();
    const o = origin.getBoundingClientRect();
    const cs = getComputedStyle(textEl);
    const style = ["font-family", "font-size", "font-weight", "font-style", "line-height", "letter-spacing", "text-transform", "text-align", "color", "padding", "background", "border", "border-radius"].map((p) => `${p}:${cs.getPropertyValue(p)}`).join(";");
    setBox({ left: (r.left - o.left) / zoom, top: (r.top - o.top) / zoom, width: r.width / zoom, minHeight: r.height / zoom, style });
    el.setAttribute("data-editing", "");
    return () => el.removeAttribute("data-editing");
  }, [container, origin, nodeId, zoom, version]);

  // Create the ProseMirror view once.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !entry) return;
    const raw = entry.node.props[prop];
    const initial: RichTextDoc = isRichTextDoc(raw) ? raw : richTextFromTemplate(typeof raw === "string" ? raw : "");
    const handle = createRichTextView({
      element: host,
      doc: initial,
      labelFor,
      singleLine,
      onEnter: onClose,
      onEscape: onClose,
      onChange: (doc) => {
        const value = singleLine ? richTextToPlain(doc) : doc;
        session.store.dispatch({ type: "setProp", id: nodeId, path: prop, value }, { label: "Edit text", mergeKey: `text:${nodeId}` });
      },
      onSelectionChange: (v) => setFormats(activeFormats(v)),
      autocompleteHandlers: () => handlersRef.current,
      onTokenTrigger: (trigger, v) => {
        if (!trigger) return setSuggest(null);
        // Anchor below the braces themselves, in the overlay's coordinates.
        const c = v.coordsAtPos(trigger.from);
        const o = origin.getBoundingClientRect();
        setSuggest({ trigger, view: v, at: { left: c.left - o.left, top: c.bottom - o.top + 4 } });
      }
    });
    viewRef.current = handle;
    handle.view.focus();
    rteCommands.selectAll(handle.view);
    setFormats(activeFormats(handle.view));
    return () => {
      handle.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, box !== null]);

  if (!entry || !box) return null;
  const view: EditorView | undefined = viewRef.current?.view;
  const f = formats;

  return (
    <>
      <div className="bpe-inline-editor" style={{ left: box.left, top: box.top, width: box.width, minHeight: box.minHeight, zoom }} onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className="bpe-inline-editor__frame" />
        <div ref={hostRef} style={cssStringToObject(box.style)} />
      </div>
      {suggest ? (
        <TokenSuggestions
          ref={handlersRef}
          trigger={suggest.trigger}
          view={suggest.view}
          dataSources={session.dataSources}
          at={suggest.at}
          onClose={() => setSuggest(null)}
        />
      ) : null}
      <div className="bpe-bubble" style={{ left: box.left * zoom, top: Math.max(0, box.top * zoom - 40) }} onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.preventDefault()}>
        {!singleLine && (
          <>
            <Button size="sm" icon="bold" tooltip="Bold" shortcut="⌘B" active={!!f?.bold} onClick={() => view && rteCommands.toggle(view, "bold")} />
            <Button size="sm" icon="italic" tooltip="Italic" shortcut="⌘I" active={!!f?.italic} onClick={() => view && rteCommands.toggle(view, "italic")} />
            <Button size="sm" icon="underline" tooltip="Underline" shortcut="⌘U" active={!!f?.underline} onClick={() => view && rteCommands.toggle(view, "underline")} />
            <Button size="sm" icon="strikethrough" tooltip="Strikethrough" active={!!f?.strike} onClick={() => view && rteCommands.toggle(view, "strike")} />
            <span className="bpe-bubble__sep" />
            <Button size="sm" icon="list" tooltip="Bullet list" active={!!f?.bulletList} onClick={() => view && rteCommands.toggleList(view, "bullet")} />
            <Button size="sm" icon="list-ordered" tooltip="Numbered list" active={!!f?.orderedList} onClick={() => view && rteCommands.toggleList(view, "ordered")} />
            <span className="bpe-bubble__sep" />
            <Button size="sm" icon="align-left" tooltip="Align left" active={!f?.align || f.align === "left"} onClick={() => view && rteCommands.setAlign(view, null)} />
            <Button size="sm" icon="align-center" tooltip="Align centre" active={f?.align === "center"} onClick={() => view && rteCommands.setAlign(view, "center")} />
            <Button size="sm" icon="align-right" tooltip="Align right" active={f?.align === "right"} onClick={() => view && rteCommands.setAlign(view, "right")} />
            <Button size="sm" icon="align-justify" tooltip="Justify" active={f?.align === "justify"} onClick={() => view && rteCommands.setAlign(view, "justify")} />
            <span className="bpe-bubble__sep" />
            <Popover placement="bottom-start" trigger={<Button size="sm" icon="link" tooltip="Link" active={!!f?.link} />}>
              {(close) => <LinkEditor value={f?.link ?? ""} onSubmit={(href) => { view && rteCommands.setLink(view, href || null); close(); }} />}
            </Popover>
            <Popover placement="bottom-start" className="bpe-color-popover" trigger={<Button size="sm" icon="palette" tooltip="Text colour" active={!!f?.color} />}>
              {(close) => <ColorPicker value={f?.color ?? ""} theme={session.activeTheme} allowTransparent={false} onChange={(c) => { view && rteCommands.setColor(view, c || null); close(); }} />}
            </Popover>
            <span className="bpe-bubble__sep" />
          </>
        )}
        <Popover placement="bottom-start" className="bpe-picker" trigger={<Button size="sm" icon="braces" tooltip="Insert data field">Field</Button>}>
          {(close) => (
            <DataPicker
              dataSources={session.dataSources}
              onPick={(expr, label) => {
                if (view) rteCommands.insertToken(view, expr, label);
                close();
              }}
            />
          )}
        </Popover>
        <Button size="sm" variant="primary" icon="check" tooltip="Done" shortcut="Esc" onClick={onClose} />
      </div>
    </>
  );
}

function LinkEditor({ value, onSubmit }: { value: string; onSubmit: (href: string) => void }) {
  const [v, setV] = useState(value);
  return (
    <div style={{ display: "flex", gap: 6, padding: 4, width: 280 }}>
      <TextInput value={v} onChange={setV} placeholder="https://… or mailto:" autoFocus onKeyDown={(e) => e.key === "Enter" && onSubmit(v)} />
      <Button variant="primary" size="sm" onClick={() => onSubmit(v)}>Apply</Button>
      {value ? <Button size="sm" icon="x" tooltip="Remove link" onClick={() => onSubmit("")} /> : null}
    </div>
  );
}

function cssStringToObject(css: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const decl of css.split(";")) {
    const i = decl.indexOf(":");
    if (i < 0) continue;
    const k = decl.slice(0, i).trim();
    const v = decl.slice(i + 1).trim();
    if (!k || !v) continue;
    out[k.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = v;
  }
  return out;
}
